import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';
import 'react-photo-view/dist/react-photo-view.css';
import { Provider } from 'react-redux';
import 'react-tagsinput/react-tagsinput.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
// Redux
// https://github.com/rt2zz/redux-persist
import { PersistGate } from 'redux-persist/integration/react';
// Axios
import axios from 'axios';
// Bootstrap Bundle JS
import 'bootstrap/dist/js/bootstrap.bundle.min';
import { Chart, registerables } from 'chart.js';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
// Bootstrap CSS
import 'bootstrap/dist/css/bootstrap.min.css';

/**
 * TIP: Replace this style import with dark styles to enable dark mode
 *
 * import './_metronic/assets/sass/style.dark.scss'
 **/
import './_metronic/assets/sass/style.scss';
import { MetronicI18nProvider } from './_metronic/i18n/Metronici18n';
// Apps
import { App } from './app/App';
import MarketingApp from './app/marketing/MarketingApp';
import store, { persistor } from './services/redux/app/store';
import * as _redux from './setup';

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;

/**
 * Yönetim paneli artık `/yonetim` uzantısı altından erişilir. Kök adres (`/`)
 * ve pazarlama alt-yolları için Apple tarzı tanıtım sitesi gösterilir.
 *
 * Admin uygulaması, iç yollarını (`/dashboard`, `/auth/login`, ...) değiştirmeye
 * gerek kalmadan `basename="/yonetim"` ile sarmalanır; böylece tüm bağlantılar
 * otomatik olarak `/yonetim` önekini alır.
 */
const ADMIN_PREFIX = `${PUBLIC_URL || ''}/yonetim`;

function isAdminPath(): boolean {
  const path = window.location.pathname;
  return path === ADMIN_PREFIX || path.startsWith(`${ADMIN_PREFIX}/`);
}

/**
 * Yasal/sözleşme gibi kamuya açık sayfalar `/yonetim` altına taşınmaz; eski
 * hallerindeki gibi doğrudan kök adresten erişilir (ör. /privacy-and-security).
 * Bu yollar admin uygulamasının public Route'ları tarafından karşılanır ama
 * kök (`PUBLIC_URL`) basename ile — yani önek almadan.
 */
const PUBLIC_ROOT_ROUTES = ['/distance-sales-contract', '/privacy-and-security', '/cancel-refund', '/support', '/campaignRequest', '/4x4-kampanya'];

function isPublicRootPath(): boolean {
  const path = window.location.pathname;
  const base = PUBLIC_URL || '';
  return PUBLIC_ROOT_ROUTES.some((r) => path === base + r || path.startsWith(`${base}${r}/`));
}
/**
 * Creates `axios-mock-adapter` instance for provided `axios` instance, add
 * basic Metronic mocks and returns it.
 *
 * @see https://github.com/ctimmerm/axios-mock-adapter
 */

// /* const mock = */ _redux.mockAxios(axios)
/**
 * Inject Metronic interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */
_redux.setupAxios(axios, store);

Chart.register(...registerables);

Sentry.init({
  dsn: 'https://5b13940f2c4ad2073a8f97282373d3f5@o4507548924313600.ingest.de.sentry.io/4507548927131728',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/api\.duguntakibi\.com\.tr/, /duguntakibi\.com\.tr/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const container = document.getElementById('root');
if (container) {
  const root = ReactDOM.createRoot(container);
  const queryClient = new QueryClient();
  const adminPath = isAdminPath();
  root.render(
    adminPath || isPublicRootPath() ? (
      <QueryClientProvider client={queryClient}>
        <MetronicI18nProvider>
          <Provider store={store}>
            {/* Asynchronously persist redux stores and show `SplashScreen` while it's loading. */}
            <PersistGate persistor={persistor} loading={<div>Yükleniyor...</div>}>
              {/* Admin `/yonetim` önekiyle; kamuya açık yasal sayfalar kök basename ile. */}
              <App basename={adminPath ? ADMIN_PREFIX : PUBLIC_URL} />
              <ToastContainer autoClose={3000} />
            </PersistGate>
          </Provider>
        </MetronicI18nProvider>
      </QueryClientProvider>
    ) : (
      <MarketingApp />
    ),
  );
} else {
  console.error('Root container missing in index.html');
}
